import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import axiosInstant from '../../../../api';
import config from '../../../../config';
import { adminAffinityProductSearch } from '../../../../api/endpoints';
import { FocusOption } from './CategoryPicker';
import { PICKER_INPUT_SX } from './affinity.constants';

interface Props {
  value: FocusOption | null;
  onChange: (value: FocusOption | null) => void;
}

// There are far too many products to preload, so this is an async typeahead
// that queries the admin product search as the admin types (debounced).
export const ProductPicker: React.FC<Props> = ({ value, onChange }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<FocusOption[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = input.trim();
    if (term.length < 2) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await axiosInstant.get(
          `${config.API_URL}${adminAffinityProductSearch(term)}`,
        );
        setOptions(res.data || []);
      } catch (err) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [input]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v) => setInput(v)}
      options={value && !options.find((o) => o.id === value.id) ? [value, ...options] : options}
      getOptionLabel={(o) => o.name || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      filterOptions={(x) => x}
      loading={loading}
      noOptionsText={input.trim().length < 2 ? 'Type to search products…' : 'No products found'}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Select Product"
          size="small"
          sx={PICKER_INPUT_SX}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ width: '100%' }}
    />
  );
};
